import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import RecurringScheduleModal from '../../../components/ui/RecurringScheduleModal';

import StarRating from '../../../components/ui/StarRating';
import RewardBadge from '../../../components/ui/RewardBadge';


const AddTaskForm = ({ persons, onAddTask, onCancel }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    personId: '',
    time: '',
    duration: '',
    priority: 'medium',
    stars: 0,
    reward: null,
    recurringConfig: null
  });

  const [errors, setErrors] = useState({});
  const [isRecurringModalOpen, setIsRecurringModalOpen] = useState(false);

  const personOptions = persons?.map(person => ({
    value: person?.id?.toString(),
    label: person?.name,
    description: person?.role
  }));

  const timeOptions = Array.from({ length: 24 }, (_, i) => {
    const period = i >= 12 ? 'PM' : 'AM';
    const displayHour = i === 0 ? 12 : i > 12 ? i - 12 : i;
    return {
      value: i?.toString(),
      label: `${displayHour}:00 ${period}`
    };
  });

  const priorityOptions = [
    { value: 'low', label: 'Low Priority' },
    { value: 'medium', label: 'Medium Priority' },
    { value: 'high', label: 'High Priority' }
  ];

  const durationOptions = [
    { value: '15', label: '15 minutes' },
    { value: '30', label: '30 minutes' },
    { value: '45', label: '45 minutes' },
    { value: '60', label: '1 hour' },
    { value: '90', label: '1.5 hours' },
    { value: '120', label: '2 hours' }
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData?.title?.trim()) {
      newErrors.title = 'Task title is required';
    }

    if (!formData?.personId) {
      newErrors.personId = 'Please assign to a family member';
    }

    if (!formData?.time) {
      newErrors.time = 'Please select a time slot';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();

    if (!validate()) {
      return;
    }

    const selectedPerson = persons?.find(p => p?.id?.toString() === formData?.personId);

    const newTask = {
      id: Date.now(),
      title: formData?.title,
      description: formData?.description,
      personId: parseInt(formData?.personId),
      personName: selectedPerson?.name,
      personColor: selectedPerson?.color,
      personInitials: selectedPerson?.initials,
      time: parseInt(formData?.time),
      duration: formData?.duration ? parseInt(formData?.duration) : null,
      priority: formData?.priority,
      completed: false,
      stars: formData?.stars,
      reward: formData?.reward,
      recurringConfig: formData?.recurringConfig,
      createdAt: new Date()?.toISOString()
    };

    onAddTask(newTask);
  };

  const handleRecurringSave = (recurringData) => {
    setFormData(prev => ({
      ...prev,
      recurringConfig: recurringData
    }));
  };

  return (
    <>
      <div className="bg-card rounded-xl border border-border p-4 md:p-5 lg:p-6">
      <div className="flex items-center justify-between mb-4 md:mb-6">
        <div className="flex items-center gap-2 md:gap-3">
          <Icon name="plus-circle" size={20} className="text-primary" />
          <h2 className="text-lg md:text-xl lg:text-2xl font-semibold text-foreground">
            Add New Task
          </h2>
        </div>
        <button
          onClick={onCancel}
          className="p-2 hover:bg-muted rounded-lg transition-smooth"
          aria-label="Close form"
        >
          <Icon name="x" size={20} className="text-muted-foreground" />
        </button>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4 md:space-y-5">
        <Input
          label="Task Title"
          type="text"
          placeholder="Enter task title"
          value={formData?.title}
          onChange={(e) => handleChange('title', e?.target?.value)}
          error={errors?.title}
          required
        />

        <Input
          label="Description (Optional)"
          type="text"
          placeholder="Add task details"
          value={formData?.description}
          onChange={(e) => handleChange('description', e?.target?.value)}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            label="Assign To"
            placeholder="Select family member"
            options={personOptions}
            value={formData?.personId}
            onChange={(value) => handleChange('personId', value)}
            error={errors?.personId}
            required
          />

          <Select
            label="Time Slot"
            placeholder="Select time"
            options={timeOptions}
            value={formData?.time}
            onChange={(value) => handleChange('time', value)}
            error={errors?.time}
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            label="Duration (Optional)"
            placeholder="Select duration"
            options={durationOptions}
            value={formData?.duration}
            onChange={(value) => handleChange('duration', value)}
          />

          <Select
            label="Priority"
            options={priorityOptions}
            value={formData?.priority}
            onChange={(value) => handleChange('priority', value)}
          />
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-foreground">
            Recurring Schedule
          </label>
          <Button
            type="button"
            variant={formData?.recurringConfig ? 'default' : 'outline'}
            onClick={() => setIsRecurringModalOpen(true)}
            className="w-full"
          >
            <Icon name="repeat" size={16} className="mr-2" />
            {formData?.recurringConfig 
              ? `Repeats ${formData?.recurringConfig?.frequency}` 
              : 'Set Recurring Schedule'}
          </Button>
          {formData?.recurringConfig && (
            <div className="flex items-center justify-between bg-muted/50 rounded-lg p-3">
              <p className="text-sm text-muted-foreground">
                {formData?.recurringConfig?.frequency === 'daily' && 'Repeats every day'}
                {formData?.recurringConfig?.frequency === 'weekly' && 
                  `Repeats every ${formData?.recurringConfig?.pattern?.daysOfWeek?.join(', ')}`}
                {formData?.recurringConfig?.frequency === 'monthly' && 
                  `Repeats on day ${formData?.recurringConfig?.pattern?.dayOfMonth} of each month`}
              </p>
              <button
                type="button"
                onClick={() => handleChange('recurringConfig', null)}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <Icon name="x" size={16} />
              </button>
            </div>
          )}
        </div>

        <div className="space-y-3">
          <label className="block text-sm font-medium text-foreground">
            Fun Rewards (Optional)
          </label>
          
          <div className="space-y-2">
            <label className="text-xs text-muted-foreground">Star Rating</label>
            <StarRating 
              value={formData?.stars} 
              onChange={(value) => handleChange('stars', value)}
              size={20}
            />
          </div>

          <div className="space-y-2">
            <label className="text-xs text-muted-foreground">Reward Badge</label>
            <div className="flex flex-wrap gap-2">
              {['trophy', 'medal', 'badge', 'star', 'crown', 'gem']?.map((rewardType) => (
                <button
                  key={rewardType}
                  type="button"
                  onClick={() => handleChange('reward', formData?.reward === rewardType ? null : rewardType)}
                  className={`transition-smooth hover:scale-105 ${
                    formData?.reward === rewardType ? 'ring-2 ring-primary rounded-full' : ''
                  }`}
                >
                  <RewardBadge reward={rewardType} size="default" />
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex flex-col-reverse sm:flex-row gap-3 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            fullWidth
            className="sm:flex-1"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="default"
            iconName="plus"
            iconPosition="left"
            fullWidth
            className="sm:flex-1"
          >
            Add Task
          </Button>
        </div>
      </form>
    </div>
      <RecurringScheduleModal
        isOpen={isRecurringModalOpen}
        onClose={() => setIsRecurringModalOpen(false)}
        onSave={handleRecurringSave}
        initialData={formData?.recurringConfig}
      />
    </>
  );
};

export default AddTaskForm;